import type { UserRef } from './api';
import type { Locale } from './i18n';

/**
 * Site analytics (operator, 2026-11-04: "add analytics button to every site to
 * know everything you can show about the website"): the shapes the API sends for
 * a site's Analytics page, and what the page works out from them. The API counts
 * what it serves — no cookie, no script added to anyone's pages — so a visitor is
 * counted once a day, robots apart (modules/sites/site-analytics-rules.ts).
 */

export const RANGES = ['24h', '7d', '30d', '90d', '12m'] as const;
export type RangeKey = (typeof RANGES)[number];
export const DEFAULT_RANGE: RangeKey = '7d';

export const TABS = ['traffic', 'requests', 'site'] as const;
export type AnalyticsTab = (typeof TABS)[number];

export interface AnalyticsSite {
  id: string;
  name: string;
  folder: string;
  address: string;
  isLive: boolean;
  searchable: boolean;
  owner: UserRef;
  createdAt: string;
  updatedAt: string;
}

export interface AnalyticsRange {
  key: RangeKey;
  from: string;
  to: string;
  previousFrom: string;
  previousTo: string;
  bucket: 'hour' | 'day' | 'month';
  timeZone: string;
  /** The company's first day of the week: 0 Sunday … 6 Saturday. */
  weekStart: number;
}

export interface Totals {
  visitors: number;
  pageViews: number;
  visits: number;
  bounces: number;
  /** Every visit's length summed: from its first page to its last. */
  seconds: number;
}

export interface Share {
  name: string | null;
  visitors: number;
  views: number;
}

export interface TrafficReport {
  site: AnalyticsSite;
  range: AnalyticsRange;
  /** The first page view counted for this site: nothing before it was recorded. */
  countingSince: string | null;
  totals: Totals;
  previous: Totals;
  series: Array<{ bucket: string; visitors: number; pageViews: number }>;
  pages: Array<{ path: string; views: number; visitors: number; seconds: number | null }>;
  entries: Array<{ path: string; visits: number; visitors: number; bounces: number }>;
  exits: Array<{ path: string; visits: number; visitors: number }>;
  sources: Array<{ name: string | null; visits: number; visitors: number; bounces: number }>;
  campaigns: Array<{ source: string | null; medium: string | null; campaign: string | null; visits: number; visitors: number }>;
  countries: Share[];
  devices: Share[];
  browsers: Share[];
  systems: Share[];
  languages: Share[];
  /** Page views by weekday (0 Sunday … 6 Saturday) and hour, on the company's clock. */
  hours: Array<{ day: number; hour: number; views: number }>;
}

export type BotKind = 'search' | 'preview' | 'ai' | 'seo' | 'monitor' | 'script' | 'other';

interface Counted {
  requests: number;
  bytes: number;
  ok: number;
  notModified: number;
  notFound: number;
  bots: number;
}

export interface RequestsReport {
  site: AnalyticsSite;
  range: AnalyticsRange;
  /** Files are counted by the company's day: for 24 hours, yesterday's and today's. */
  fileDays: { from: string; to: string };
  pages: Counted;
  files: Counted;
  daily: Array<{ day: string; requests: number; bytes: number }>;
  speed: { pages: number; average: number | null; median: number | null; p95: number | null; slowest: number | null };
  notFound: Array<{ path: string; page: boolean; requests: number; lastAt: string | null; lastDay: string }>;
  bots: Array<{ name: string; kind: BotKind; views: number; pages: number; last: string }>;
  topFiles: Array<{ path: string; requests: number; bytes: number; cached: number }>;
}

export interface LiveReport {
  site: AnalyticsSite;
  minutes: number;
  visitors: number;
  views: number;
  pages: Array<{ path: string; visitors: number }>;
  lastVisit: { at: string; path: string } | null;
}

export type CheckId =
  | 'front-page'
  | 'broken-links'
  | 'outside-links'
  | 'insecure'
  | 'titles'
  | 'duplicate-titles'
  | 'descriptions'
  | 'viewport'
  | 'lang'
  | 'alt'
  | 'icon'
  | 'large-files'
  | 'not-found-page'
  | 'unused';

export type CheckLevel = 'error' | 'warning' | 'info' | 'pass';

export interface CheckItem {
  page?: string;
  target?: string;
  path?: string;
  size?: number;
  title?: string;
  pages?: string[];
}

export type ContentKind = 'pages' | 'styles' | 'scripts' | 'images' | 'fonts' | 'media' | 'documents' | 'data';

export interface SiteReport {
  site: AnalyticsSite;
  content: {
    files: number;
    bytes: number;
    folders: number;
    kinds: Array<{ kind: ContentKind; files: number; bytes: number }>;
    largest: Array<{ path: string; size: number }>;
    recent: Array<{ path: string; size: number; updatedAt: string; by: UserRef | null }>;
    createdAt: string | null;
    createdBy: UserRef | null;
    topFolders: Array<{ folder: string; files: number; bytes: number }>;
  };
  checks: Array<{ id: CheckId; level: CheckLevel; count: number; items: CheckItem[] }>;
  pages: Array<{ path: string; title: string | null; size: number }>;
  thirdParty: Array<{ host: string; kinds: string[]; pages: number; insecure: boolean }>;
  outbound: Array<{ host: string; links: number }>;
  scanned: { pages: number; styles: number; skipped: number; at: string };
  changes: Array<{
    id: string;
    action: string;
    at: string;
    by: UserRef | null;
    path?: string;
    from?: string;
    files?: number;
    fields?: string[];
  }>;
}

// ---------------------------------------------------------------------------
// Reading the figures
// ---------------------------------------------------------------------------

const LOCALE_TAG: Record<Locale, string> = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' };

/** How a page reads: `index.html` → `/`, `docs/index.html` → `/docs/`, `about.html` → `/about.html`. */
export function pageAddress(path: string): string {
  if (path === 'index.html') return '/';
  if (path.endsWith('/index.html')) return `/${path.slice(0, -'index.html'.length)}`;
  return `/${path}`;
}

/** A page's own address on the site, to open it: the site's, then the page, each part escaped. */
export function pageLink(siteAddress: string, path: string): string {
  const page = path === 'index.html' ? '' : path.endsWith('/index.html') ? path.slice(0, -'index.html'.length) : path;
  return siteAddress + page.split('/').map(encodeURIComponent).join('/');
}

/** A share of a whole, 0–100, one decimal; 0 when the whole is nothing. */
export function percentOf(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;
}

/** The change from one period to the next, in percent; null when there was nothing before. */
export function changeOf(now: number, before: number): number | null {
  if (!before) return null;
  return Math.round(((now - before) / before) * 1000) / 10;
}

/** "Egypt" / "مصر" from `EG`, the browser's own names; the code itself when it has none. */
export function countryName(code: string, locale: Locale): string {
  try {
    return new Intl.DisplayNames([LOCALE_TAG[locale]], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** "Arabic" / "العربية" from `ar`. */
export function languageName(code: string, locale: Locale): string {
  try {
    return new Intl.DisplayNames([LOCALE_TAG[locale]], { type: 'language' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** "2m 05s" / "2 د 5 ث" — a visit's or a page's time; "0s" for none. */
export function formatSpan(seconds: number | null | undefined, locale: Locale): string {
  const total = Math.max(0, Math.round(seconds ?? 0));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (locale === 'ar') {
    if (h > 0) return `${h} س ${m} د`;
    return m > 0 ? `${m} د ${s} ث` : `${s} ث`;
  }
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

/** "1.2K", "12K", "3.4M" for an axis; in full below 10,000. */
export function compactNumber(value: number, locale: Locale): string {
  if (Math.abs(value) < 10_000) return new Intl.NumberFormat(LOCALE_TAG[locale]).format(value);
  return new Intl.NumberFormat(LOCALE_TAG[locale], { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/**
 * A bucket's label, on the company's clock: `2026-11-04T14` → "14:00", a day →
 * "4 Nov", a month → "Nov 2026"; `long` for the tooltip — "4 Nov, 14:00–15:00",
 * "Wednesday 4 November", "November 2026".
 */
export function bucketLabel(bucket: string, kind: AnalyticsRange['bucket'], locale: Locale, long = false): string {
  const tag = LOCALE_TAG[locale];
  if (kind === 'hour') {
    const [day, hour] = bucket.split('T');
    const next = String((Number(hour) + 1) % 24).padStart(2, '0');
    if (!long) return `${hour}:00`;
    const date = new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${day}T12:00:00Z`));
    return `${date}, ${hour}:00–${next}:00`;
  }
  if (kind === 'day') {
    const options: Intl.DateTimeFormatOptions = long ? { weekday: 'long', day: 'numeric', month: 'long' } : { day: 'numeric', month: 'short' };
    return new Intl.DateTimeFormat(tag, { ...options, timeZone: 'UTC' }).format(new Date(`${bucket}T12:00:00Z`));
  }
  return new Intl.DateTimeFormat(tag, { month: long ? 'long' : 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${bucket}-15T12:00:00Z`));
}

/** A calendar day (`2026-11-04`) as "4 Nov 2026". */
export function dayLabel(day: string, locale: Locale): string {
  return new Intl.DateTimeFormat(LOCALE_TAG[locale], { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${day}T12:00:00Z`),
  );
}
